// 剧本生成任务详情页
// 展示生成进度和实时日志，支持轮询（核心页面）

import React, { useEffect, useState, useCallback, useRef } from 'react'
import {
  Card,
  Descriptions,
  Progress,
  Button,
  Space,
  Tag,
  message,
  Spin,
  Alert,
  Tabs,
  Empty,
  Tooltip,
} from 'antd'
import {
  ArrowLeftOutlined,
  ReloadOutlined,
  EyeOutlined,
  StopOutlined,
  PauseOutlined,
  PlayCircleOutlined,
  ExclamationCircleOutlined,
  RocketOutlined,
} from '@ant-design/icons'
import { useParams, useNavigate } from 'react-router-dom'
import type { TabsProps } from 'antd'
import {
  getScriptGenerationTaskDetail,
  getScriptGenerationTaskStatus,
  cancelScriptGenerationTask,
  pauseScriptGenerationTask,
  resumeScriptGenerationTask,
  retryScriptGenerationTaskFromPhase,
  retryScriptGenerationTask,
  startPendingScriptGenerationTask,
} from '@/services/scriptGeneration'
import type { ScriptGenerationTaskDetailResponse, PhaseResult } from '@/types/scriptGeneration'
import {
  STATUS_COLOR,
  STATUS_LABEL,
  SCRIPT_GENERATION_CONFIG,
  isRunningStatus,
  isTerminalStatus,
  isCancellableStatus,
} from '@/constants/scriptGeneration'
import { handleApiError } from '@/utils/errorHandler'
import PhaseProgress from './components/PhaseProgress'
import { PhaseResultCard } from './components/PhaseOutputViewer'
import AICallLogs from './components/AICallLogs'
import { DebugV5Data } from './components/debug_v5_data'
import './TaskDetail.css'

/**
 * 剧本生成任务详情页
 */
const ScriptGenerationTaskDetail: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>()
  const navigate = useNavigate()
  const [messageApi, contextHolder] = message.useMessage()

  // 状态管理
  const [taskDetail, setTaskDetail] = useState<ScriptGenerationTaskDetailResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [polling, setPolling] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [pausing, setPausing] = useState(false)
  const [resuming, setResuming] = useState(false)
  const [forceStarting, setForceStarting] = useState(false)

  // 使用 ref 跟踪组件挂载状态和轮询定时器
  const isMountedRef = useRef(true)
  const pollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  // 使用 ref 存储前一个状态，避免依赖循环
  const prevStatusRef = useRef<string | undefined>(undefined)

  /**
   * 处理无效 taskId - 导航回列表页
   */
  useEffect(() => {
    if (!taskId) {
      messageApi.error('无效的任务 ID')
      navigate('/script-generation', { replace: true })
    }
  }, [taskId, navigate, messageApi])

  /**
   * 加载任务详情
   */
  const loadTaskDetail = useCallback(async () => {
    if (!taskId || !isMountedRef.current) return null

    console.log(`[TaskDetail] 加载任务详情: taskId=${taskId}`)
    setLoading(true)
    try {
      const detail = await getScriptGenerationTaskDetail(taskId)
      if (isMountedRef.current) {
        console.log(`[TaskDetail] 任务详情加载成功: status=${detail?.status}, progress=${detail?.progress}%`)
        setTaskDetail(detail)
      }
      return detail
    } catch (error) {
      console.error(`[TaskDetail] 加载任务详情失败: taskId=${taskId}`, error)
      if (isMountedRef.current) {
        handleApiError(error, '加载任务详情失败', messageApi)
      }
      return null
    } finally {
      if (isMountedRef.current) {
        setLoading(false)
      }
    }
  }, [taskId, messageApi])

  /**
   * 轻量级状态轮询（仅获取状态和进度）
   */
  const loadTaskStatus = useCallback(async () => {
    if (!taskId || !isMountedRef.current) return null

    console.log(`[TaskDetail] 开始状态轮询: taskId=${taskId}`)
    // 创建新的 AbortController 用于取消请求
    abortControllerRef.current = new AbortController()

    try {
      const status = await getScriptGenerationTaskStatus(taskId)
      if (!isMountedRef.current) return null

      console.log(`[TaskDetail] 状态轮询结果: status=${status.status}, progress=${status.progress}%, phase=${status.current_phase}`)

      // 检查是否是新变为终态（用于显示通知）
      const prevStatus = prevStatusRef.current
      const isNewlyFailed = status.status === 'failed' && prevStatus !== 'failed'
      const isNewlyCompleted = status.status === 'completed' && prevStatus !== 'completed'
      const isNewlyCancelled = status.status === 'cancelled' && prevStatus !== 'cancelled'

      console.log(`[TaskDetail] 状态变化检测: prev=${prevStatus}, current=${status.status}, isNewlyFailed=${isNewlyFailed}, isNewlyCompleted=${isNewlyCompleted}`)

      // 更新前一个状态记录
      prevStatusRef.current = status.status

      // 如果任务失败，显示错误通知
      if (isNewlyFailed && status.error_message) {
        console.error(`[TaskDetail] 任务执行失败: ${status.error_message}`)
        messageApi.error({
          content: `任务执行失败：${status.error_message}`,
          duration: 5,
        })
      }

      // 如果任务成功完成，显示成功通知
      if (isNewlyCompleted) {
        console.log('[TaskDetail] 任务成功完成')
        messageApi.success({
          content: '剧本生成任务已完成！',
          duration: 3,
        })
      }

      // 如果任务被取消，显示取消通知
      if (isNewlyCancelled) {
        console.log('[TaskDetail] 任务已取消')
        messageApi.warning({
          content: '任务已取消',
          duration: 3,
        })
      }

      // 更新当前任务状态
      setTaskDetail((prev) => {
        if (!prev) return prev

        // 检查是否应该停止轮询
        if (status.status && isTerminalStatus(status.status as any)) {
          console.log(`[TaskDetail] 任务进入终态，停止轮询: status=${status.status}`)
          // 停止轮询后加载完整详情
          setTimeout(() => {
            if (isMountedRef.current) {
              loadTaskDetail()
            }
          }, 0)
        }

        return {
          ...prev,
          status: status.status as any,
          progress: status.progress,
          currentPhase: status.current_phase,
          phaseName: status.phase_name,
          errorMessage: status.error_message,
          // 更新阶段结果（用于实时显示产物）- 使用类型断言避免类型不匹配
          phaseResults: (status.phase_results || prev.phaseResults) as any,
        }
      })

      return status
    } catch (error) {
      console.error(`[TaskDetail] 状态轮询失败: taskId=${taskId}`, error)
      return null
    } finally {
      // 清理 AbortController
      abortControllerRef.current = null
    }
  }, [taskId, loadTaskDetail, messageApi])

  /**
   * 停止轮询
   */
  const stopPolling = useCallback(() => {
    console.log('[TaskDetail] 停止轮询')
    // 清除定时器
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current)
      pollingTimerRef.current = null
    }

    // 取消正在进行的请求
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }

    setPolling(false)
  }, [])

  /**
   * 带指数退避的轮询
   * 随着时间推移逐渐增加轮询间隔，减少服务器负载
   */
  const startPolling = useCallback(() => {
    console.log(`[TaskDetail] 启动轮询: baseInterval=${SCRIPT_GENERATION_CONFIG.POLL_INTERVAL}ms`)
    // 先停止之前的轮询
    stopPolling()

    setPolling(true)

    // 轮询状态管理
    let pollCount = 0
    const baseInterval = SCRIPT_GENERATION_CONFIG.POLL_INTERVAL
    const maxInterval = 10000 // 最大间隔 10 秒

    /**
     * 执行单次轮询
     * @param currentInterval - 当前轮询间隔
     */
    const poll = async (currentInterval: number) => {
      if (!isMountedRef.current) {
        console.log('[TaskDetail] 组件已卸载，停止轮询')
        stopPolling()
        return
      }

      try {
        const status = await loadTaskStatus()
        if (!isMountedRef.current) {
          console.log('[TaskDetail] 组件已卸载，停止轮询')
          stopPolling()
          return
        }

        // 检查是否应该停止轮询
        if (status && isTerminalStatus(status.status as any)) {
          console.log(`[TaskDetail] 任务进入终态，停止轮询: status=${status.status}`)
          stopPolling()
          return
        }

        // 计算下次轮询间隔（指数退避，但不超过 maxInterval）
        pollCount++
        const nextInterval = Math.min(
          baseInterval * Math.pow(1.5, pollCount),
          maxInterval
        )

        console.log(`[TaskDetail] 下次轮询间隔: ${Math.round(nextInterval)}ms (pollCount=${pollCount})`)

        // 继续下一次轮询
        pollingTimerRef.current = setTimeout(() => {
          poll(nextInterval)
        }, currentInterval)
      } catch (error) {
        console.error(`[TaskDetail] 轮询执行出错，${baseInterval}ms后重试:`, error)
        // 出错时仍继续轮询，但使用基础间隔
        if (isMountedRef.current) {
          pollingTimerRef.current = setTimeout(() => {
            poll(baseInterval)
          }, baseInterval)
        }
      }
    }

    // 启动首次轮询
    pollingTimerRef.current = setTimeout(() => {
      poll(baseInterval)
    }, baseInterval)
  }, [loadTaskStatus, stopPolling])

  /**
   * 初始化加载数据
   */
  useEffect(() => {
    console.log(`[TaskDetail] 组件初始化: taskId=${taskId}`)
    const init = async () => {
      const detail = await loadTaskDetail()
      if (detail && isMountedRef.current && detail.status) {
        console.log(`[TaskDetail] 初始化状态: status=${detail.status}, isRunning=${isRunningStatus(detail.status as any)}`)
        // 如果任务正在进行中，启动轮询
        if (isRunningStatus(detail.status as any)) {
          startPolling()
        }
        // 初始化前一个状态记录，避免刷新时重复通知
        prevStatusRef.current = detail.status
      }
    }

    init()

    // 清理函数
    return () => {
      console.log('[TaskDetail] 组件卸载，清理资源')
      isMountedRef.current = false
      stopPolling()
    }
  }, [loadTaskDetail, startPolling, stopPolling])

  /**
   * 返回列表页
   */
  const handleBack = useCallback(() => {
    navigate('/script-generation')
  }, [navigate])

  /**
   * 查看配置预览
   */
  const handlePreview = useCallback(() => {
    if (taskId) {
      navigate(`/script-generation/${taskId}/preview`)
    }
  }, [taskId, navigate])

  /**
   * 取消任务
   */
  const handleCancel = useCallback(async () => {
    if (!taskId) return

    console.log(`[TaskDetail] 取消任务: taskId=${taskId}`)
    setCancelling(true)
    try {
      await cancelScriptGenerationTask(taskId)
      console.log(`[TaskDetail] 任务取消成功: taskId=${taskId}`)
      messageApi.success('任务已取消')
      stopPolling()
      await loadTaskDetail()
    } catch (error) {
      console.error(`[TaskDetail] 取消任务失败: taskId=${taskId}`, error)
      handleApiError(error, '取消任务失败', messageApi)
    } finally {
      setCancelling(false)
    }
  }, [taskId, messageApi, stopPolling, loadTaskDetail])

  /**
   * 暂停任务
   */
  const handlePause = useCallback(async () => {
    if (!taskId) return

    console.log(`[TaskDetail] 暂停任务: taskId=${taskId}`)
    setPausing(true)
    try {
      await pauseScriptGenerationTask(taskId)
      console.log(`[TaskDetail] 任务暂停成功: taskId=${taskId}`)
      messageApi.success('任务已暂停')
      stopPolling()
      await loadTaskDetail()
    } catch (error) {
      console.error(`[TaskDetail] 暂停任务失败: taskId=${taskId}`, error)
      handleApiError(error, '暂停任务失败', messageApi)
    } finally {
      setPausing(false)
    }
  }, [taskId, messageApi, stopPolling, loadTaskDetail])

  /**
   * 恢复任务
   */
  const handleResume = useCallback(async () => {
    if (!taskId) return

    console.log(`[TaskDetail] 恢复任务: taskId=${taskId}`)
    setResuming(true)
    try {
      await resumeScriptGenerationTask(taskId)
      console.log(`[TaskDetail] 任务恢复成功: taskId=${taskId}`)
      messageApi.success('任务已恢复')
      startPolling()
      await loadTaskDetail()
    } catch (error) {
      console.error(`[TaskDetail] 恢复任务失败: taskId=${taskId}`, error)
      handleApiError(error, '恢复任务失败', messageApi)
    } finally {
      setResuming(false)
    }
  }, [taskId, messageApi, startPolling, loadTaskDetail])

  /**
   * 手动刷新
   */
  const handleRefresh = useCallback(() => {
    console.log(`[TaskDetail] 手动刷新任务详情: taskId=${taskId}`)
    loadTaskDetail()
  }, [loadTaskDetail, taskId])

  /**
   * 从指定阶段重试
   */
  const handleRetryPhase = useCallback(async (phase: number) => {
    if (!taskId) return

    console.log(`[TaskDetail] 从阶段 ${phase} 重试: taskId=${taskId}`)
    try {
      await retryScriptGenerationTaskFromPhase(taskId, phase)
      console.log(`[TaskDetail] 阶段重试成功: taskId=${taskId}, phase=${phase}`)
      messageApi.success(`已从阶段 ${phase} 开始重试`)
      // 重新加载任务详情并启动轮询
      await loadTaskDetail()
      startPolling()
    } catch (error) {
      console.error(`[TaskDetail] 阶段重试失败: taskId=${taskId}, phase=${phase}`, error)
      handleApiError(error, '重试阶段失败', messageApi)
    }
  }, [taskId, messageApi, loadTaskDetail, startPolling])

  /**
   * 智能重试任务（从失败的阶段继续）
   */
  const handleRetryAll = useCallback(async () => {
    if (!taskId) return

    console.log(`[TaskDetail] 智能重试任务: taskId=${taskId}`)
    try {
      const result = await retryScriptGenerationTask(taskId)
      console.log(`[TaskDetail] 智能重试成功: taskId=${taskId}`, result)
      // 显示智能重试信息
      const phaseInfo = result.estimatedDuration
        ? `预计耗时 ${result.estimatedDuration}`
        : ''
      messageApi.success(`任务已智能重试：${result.message}${phaseInfo ? ` (${phaseInfo})` : ''}`)
      // 重新加载任务详情并启动轮询
      await loadTaskDetail()
      startPolling()
    } catch (error: any) {
      // 显示详细错误信息
      const errorMsg = error?.response?.data?.detail || error?.message || '未知错误'
      console.error(`[TaskDetail] 智能重试失败: taskId=${taskId}, error=${errorMsg}`)
      messageApi.error(`重试失败: ${errorMsg}`)
    }
  }, [taskId, messageApi, loadTaskDetail, startPolling])

  /**
   * 强制启动 pending 状态的任务（使用默认配置）
   */
  const handleForceStart = useCallback(async () => {
    if (!taskId || !taskDetail) return

    console.log(`[TaskDetail] 强制启动任务: taskId=${taskId}, version=${taskDetail.version}`)
    setForceStarting(true)
    try {
      const result = await startPendingScriptGenerationTask(taskId, taskDetail.version || '5.0')
      console.log(`[TaskDetail] 强制启动成功: taskId=${taskId}`, result)
      messageApi.success('任务已启动，使用默认配置')
      // 重新加载任务详情并启动轮询
      await loadTaskDetail()
      startPolling()
    } catch (error: any) {
      const errorMsg = error?.response?.data?.detail || error?.message || '未知错误'
      console.error(`[TaskDetail] 强制启动失败: taskId=${taskId}, error=${errorMsg}`)
      handleApiError(error, '启动任务失败', messageApi)
    } finally {
      setForceStarting(false)
    }
  }, [taskId, taskDetail, messageApi, loadTaskDetail, startPolling])

  // 早期返回：处理无效 taskId
  if (!taskId) {
    return null
  }

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    )
  }

  if (!taskDetail) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Empty description="任务不存在" />
      </div>
    )
  }

  // 提前定义状态变量，供 Tab 配置使用
  const isTaskCompleted = taskDetail.status === 'completed'
  const isTaskFailed = taskDetail.status === 'failed'
  const isTaskPaused = taskDetail.status === 'paused'
  const isTaskPending = taskDetail.status === 'pending'
  const isTaskCancellable = isCancellableStatus(taskDetail.status)
  const isTaskPausable = isCancellableStatus(taskDetail.status) && !isTaskPaused
  const isTaskResumable = isTaskPaused

  /**
   * Tab 配置（必须在状态变量定义之后）
   */
  const tabItems: TabsProps['items'] = [
    {
      key: 'overview',
      label: '概览',
      children: taskDetail ? (
        <div>
          {/* Pending 状态提示 */}
          {isTaskPending && (
            <Alert
              message="任务待启动"
              description={
                <span>
                  此任务已创建但未启动。点击右上角的"开始执行"按钮使用默认配置启动任务，
                  或<Button type="link" size="small" onClick={() => navigate(`/script-generation/create-v5`)} style={{ padding: 0 }}>重新创建任务</Button>进行完整配置。
                </span>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 24 }}
              action={
                <Button type="primary" size="small" onClick={handleForceStart} loading={forceStarting}>
                  立即启动
                </Button>
              }
            />
          )}

          {/* 任务基本信息 */}
          <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
            <Descriptions.Item label="任务名称" span={2}>
              {taskDetail.task_name}
            </Descriptions.Item>
            <Descriptions.Item label="任务描述" span={2}>
              {taskDetail.task_description || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="输入类型">
              {taskDetail.input_type === 'text' ? '文本输入' : '文档上传'}
            </Descriptions.Item>
            <Descriptions.Item label="状态">
              <Tag color={STATUS_COLOR[taskDetail.status]}>
                {STATUS_LABEL[taskDetail.status]}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="总体进度" span={2}>
              <Progress percent={taskDetail.progress} size="small" status={isTaskCompleted ? 'success' : isTaskFailed ? 'exception' : 'active'} />
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {new Date(taskDetail.created_at).toLocaleString('zh-CN')}
            </Descriptions.Item>
            {taskDetail.completed_at && (
              <Descriptions.Item label="完成时间" span={2}>
                {new Date(taskDetail.completed_at).toLocaleString('zh-CN')}
              </Descriptions.Item>
            )}
            {taskDetail.error_message && (
              <Descriptions.Item label="错误信息" span={2}>
                <Alert message={taskDetail.error_message} type="error" />
              </Descriptions.Item>
            )}
          </Descriptions>

          {/* 阶段进度 - 左右分栏布局 */}
          <PhaseProgress
            currentPhase={taskDetail.current_phase || 0}
            progress={taskDetail.progress}
            phaseResults={taskDetail.phase_results || []}
            aiCallLogs={taskDetail.ai_call_logs || []}
            taskStatus={taskDetail.status}
            version={taskDetail.version}
            onRetryPhase={handleRetryPhase}
            onRetryAll={handleRetryAll}
          />
        </div>
      ) : <Spin />,
    },
    {
      key: 'logs',
      label: (
        <span>
          阶段产物
          {polling && <Spin size="small" style={{ marginLeft: 8 }} />}
        </span>
      ),
      children: taskDetail ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {taskDetail.phase_results && taskDetail.phase_results.length > 0 ? (
            taskDetail.phase_results.map((phaseResult: PhaseResult, index: number) => (
              <PhaseResultCard
                key={index}
                phaseResult={phaseResult}
                aiCallLogs={taskDetail.ai_call_logs || []}
              />
            ))
          ) : taskDetail.ai_call_logs && taskDetail.ai_call_logs.length > 0 ? (
            // 对于运行中任务，从 aiCallLogs 展示实际内容
            <Card title="AI 调用产物">
              <AICallLogs
                logs={taskDetail.ai_call_logs}
                loading={loading}
                compact
              />
            </Card>
          ) : (
            <Card>
              <Empty description="暂无阶段产物数据" />
            </Card>
          )}
        </div>
      ) : <Spin />,
    },
    {
      key: 'ai-calls',
      label: (
        <span>
          AI 调用记录
          {taskDetail?.ai_call_logs?.length ? (
            <Tag color="blue" style={{ marginLeft: 4 }}>
              {taskDetail.ai_call_logs.length}
            </Tag>
          ) : null}
        </span>
      ),
      children: taskDetail ? (
        <AICallLogs
          logs={taskDetail.ai_call_logs || []}
          loading={loading}
        />
      ) : <Spin />,
    },
    // DEBUG: 数据结构调试 Tab（仅开发环境显示）
    ...(process.env.NODE_ENV === 'development' ? [{
      key: 'debug',
      label: '🔧 调试',
      children: taskDetail ? (
        <DebugV5Data phaseResults={taskDetail.phase_results || []} />
      ) : <Spin />,
    }] : []),
  ]

  return (
    <div className="script-generation-task-detail">
      {contextHolder}

      <Card
        title={
          <Space>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={handleBack}>
              返回
            </Button>
            <span>{taskDetail.task_name}</span>
            <Tag color="default" style={{ fontSize: 12, fontWeight: 'normal' }}>
              #{taskId}
            </Tag>
            <Tag color={STATUS_COLOR[taskDetail.status]}>
              {STATUS_LABEL[taskDetail.status]}
            </Tag>
            {polling && (
              <Tag color="processing" icon={<Spin size="small" />}>
                实时更新中
              </Tag>
            )}
          </Space>
        }
        extra={
          <Space>
            {isTaskPending && (
              <Tooltip title="使用默认配置启动任务">
                <Button
                  type="primary"
                  icon={<RocketOutlined />}
                  onClick={handleForceStart}
                  loading={forceStarting}
                >
                  开始执行
                </Button>
              </Tooltip>
            )}
            {isTaskPausable && (
              <Tooltip title="暂停生成">
                <Button
                  icon={<PauseOutlined />}
                  onClick={handlePause}
                  loading={pausing}
                >
                  暂停
                </Button>
              </Tooltip>
            )}
            {isTaskResumable && (
              <Tooltip title="恢复生成">
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={handleResume}
                  loading={resuming}
                >
                  恢复
                </Button>
              </Tooltip>
            )}
            {isTaskCancellable && (
              <Tooltip title="停止生成">
                <Button
                  danger
                  icon={<StopOutlined />}
                  onClick={handleCancel}
                  loading={cancelling}
                >
                  取消
                </Button>
              </Tooltip>
            )}
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
              刷新
            </Button>
            {isTaskCompleted && (
              <Button type="primary" icon={<EyeOutlined />} onClick={handlePreview}>
                查看配置
              </Button>
            )}
          </Space>
        }
      >
        {/* 进度条 */}
        <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontWeight: 600 }}>生成进度</span>
            <span style={{ color: '#666' }}>{taskDetail.progress}%</span>
          </div>
          <Progress
            percent={taskDetail.progress}
            status={isTaskCompleted ? 'success' : isTaskFailed ? 'exception' : 'active'}
            size="small"
          />
          {taskDetail.current_phase && (
            <div style={{ color: '#666', fontSize: 12, marginTop: 8 }}>
              当前阶段: {taskDetail.current_phase}
            </div>
          )}

          {/* 错误信息显示 */}
          {(isTaskFailed || taskDetail.error_message) && (
            <div style={{
              marginTop: 12,
              padding: '8px 12px',
              background: '#fff2f0',
              border: '1px solid #ffccc7',
              borderRadius: 4,
              color: '#cf1322',
              fontSize: 13
            }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>
                <ExclamationCircleOutlined style={{ marginRight: 6 }} />
                执行出错
              </div>
              <div>{taskDetail.error_message || '未知错误'}</div>
              <div style={{ marginTop: 6, fontSize: 12, color: '#999' }}>
                点击「重试」按钮从失败阶段继续执行
              </div>
            </div>
          )}
        </div>

        {/* Tab 内容 */}
        <Tabs items={tabItems} />
      </Card>
    </div>
  )
}

export default ScriptGenerationTaskDetail
